import { useState } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { ConfirmModal } from '../components/ConfirmModal';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';

interface Props {
  user: AuthUser;
}

interface ToastState {
  message: string;
  variant: ToastVariant;
}

interface ResetResult {
  success:    boolean;
  durationMs: number;
}

const isSuperAdmin = (user: AuthUser) =>
  user.roles.some((r) => r.role === 'super_admin' && r.schoolId === null);

const WHAT_RESETS = [
  ['🔑', 'Kata laluan + akaun demo', '4 pengguna — admin, guru, ibubapa, moe'],
  ['🏫', 'Tahun persekolahan 2026', 'SMAS Ali — 3 kelas (data transaksi dipadam)'],
  ['🎓', '2 rekod pelajar demo', 'DEMO-STU-001, DEMO-STU-002'],
  ['📋', '1 rekod pendidik demo', 'DEMO-EMP-001'],
] as const;

const WHAT_PRESERVED = [
  '200 sekolah dalam pangkalan data',
  'Semua pengguna bukan demo',
  'Konfigurasi sistem',
] as const;

export function DemoControlsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [modalOpen,  setModalOpen]  = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState<ToastState | null>(null);
  const [lastReset,  setLastReset]  = useState<{ at: Date; durationMs: number } | null>(null);

  const superAdmin = isSuperAdmin(user);

  async function handleReset() {
    setLoading(true);
    try {
      const result = await apiPost<ResetResult>('/api/admin/demo/reset');

      if (!result.success) {
        // Server returned 200 but reported failure — treat as error
        throw new Error('Pelayan melaporkan kegagalan tanpa butiran. / Server reported failure without details.');
      }

      setModalOpen(false);
      setLastReset({ at: new Date(), durationMs: result.durationMs });
      setToast({
        variant: 'success',
        message: `Data demo telah ditetapkan semula (${result.durationMs} ms). / Demo data restored in ${result.durationMs} ms.`,
      });
    } catch (err) {
      setModalOpen(false);

      let msg: string;
      if (err instanceof TypeError && err.message.includes('fetch')) {
        // Network-level failure (server unreachable)
        msg = 'Tidak dapat menghubungi pelayan. Semak sambungan rangkaian. / Cannot reach server. Check network connection.';
      } else if (err instanceof Error) {
        msg = err.message;
      } else {
        msg = 'Ralat tidak diketahui. / Unknown error.';
      }

      setToast({ variant: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div
        style={{
          minHeight:       '100vh',
          backgroundColor: colors.neutral.black,
          fontFamily:      typography.fontFamily.body,
          display:         'flex',
          flexDirection:   'column',
          alignItems:      'center',
          padding:         `${spacing.xl} ${spacing.md}`,
        }}
      >
        {/* ── Nav ────────────────────────────────────────────────────────── */}
        <div style={{ width: '100%', maxWidth: '560px', marginBottom: spacing.xl }}>
          <Link
            to="/"
            style={{
              color:          colors.neutral.gray400,
              fontSize:       typography.fontSize.sm,
              textDecoration: 'none',
              display:        'inline-flex',
              alignItems:     'center',
              gap:            spacing.xs,
            }}
          >
            ← Kembali ke Laman Utama / Back to Home
          </Link>
        </div>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{ width: '100%', maxWidth: '560px', marginBottom: spacing.xl }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
            <div
              style={{
                display:         'inline-flex',
                alignItems:      'center',
                justifyContent:  'center',
                width:           '36px',
                height:          '36px',
                backgroundColor: colors.primary.yellow,
                borderRadius:    borderRadius.md,
                flexShrink:      0,
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: typography.fontWeight.bold, color: colors.neutral.black }}>S</span>
            </div>
            <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
              {E ? 'Demo Controls' : 'Kawalan Demo'}
            </h1>
          </div>
          <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
            Pentadbiran data persekitaran demo · Demo environment administration
          </p>
        </div>

        {/* ── Card ───────────────────────────────────────────────────────── */}
        <div
          style={{
            width:           '100%',
            maxWidth:        '560px',
            backgroundColor: colors.neutral.charcoal,
            borderRadius:    borderRadius.xl,
            padding:         spacing.xl,
            boxShadow:       shadows.xl,
          }}
        >
          {!superAdmin ? (
            <div
              style={{
                textAlign:       'center',
                padding:         spacing.xl,
                backgroundColor: 'rgba(239,68,68,0.08)',
                borderRadius:    borderRadius.lg,
                border:          '1px solid rgba(239,68,68,0.25)',
              }}
            >
              <p style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSize.base, color: colors.neutral.white, fontWeight: typography.fontWeight.semibold }}>
                Akses Ditolak / Access Denied
              </p>
              <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                Halaman ini hanya untuk Pegawai KPM (super_admin).
                <br />This page is restricted to MOE Officials (super_admin) only.
              </p>
            </div>
          ) : (
            <>
              {/* Last reset badge */}
              {lastReset && (
                <div
                  style={{
                    display:         'flex',
                    alignItems:      'center',
                    gap:             spacing.sm,
                    marginBottom:    spacing.lg,
                    padding:         `${spacing.xs} ${spacing.sm}`,
                    backgroundColor: 'rgba(34,197,94,0.08)',
                    border:          '1px solid rgba(34,197,94,0.3)',
                    borderRadius:    borderRadius.md,
                  }}
                >
                  <span style={{ color: '#22c55e', fontSize: '14px' }}>✓</span>
                  <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                    Set semula terakhir: {lastReset.at.toLocaleTimeString('ms-BN')} · {lastReset.durationMs} ms
                    {' / '}Last reset: {lastReset.at.toLocaleTimeString('en-GB')} · {lastReset.durationMs} ms
                  </span>
                </div>
              )}

              {/* Section heading */}
              <div style={{ marginBottom: spacing.lg }}>
                <h2 style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                  Set Semula Data Demo
                </h2>
                <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                  Reset Demo Data — idempotent, selesai dalam &lt;10 s / completes in &lt;10 s
                </p>
              </div>

              {/* What will be reset */}
              <div
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius:    borderRadius.md,
                  padding:         spacing.md,
                  marginBottom:    spacing.md,
                }}
              >
                <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Yang ditetapkan semula · What resets
                </p>
                {WHAT_RESETS.map(([icon, label, sub]) => (
                  <div key={label} style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.xs, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '15px', lineHeight: 1.5, flexShrink: 0 }} aria-hidden>{icon}</span>
                    <div>
                      <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.white }}>{label}</span>
                      <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginLeft: spacing.xs }}>{sub}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* What is preserved */}
              <div
                style={{
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  borderRadius:    borderRadius.md,
                  padding:         spacing.md,
                  marginBottom:    spacing.xl,
                }}
              >
                <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Yang dipelihara · What is preserved
                </p>
                {WHAT_PRESERVED.map((item) => (
                  <div key={item} style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.xs, alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#22c55e' }} aria-hidden>✓</span>
                    <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => setModalOpen(true)}
                disabled={loading}
                loading={loading}
              >
                {loading
                  ? 'Menetapkan semula… / Resetting…'
                  : 'Set Semula Data Demo / Reset Demo Data'}
              </Button>

              <p style={{ margin: `${spacing.sm} 0 0`, textAlign: 'center', fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                Transaksi penuh — rollback automatik sekiranya gagal.
                <br />Full transaction — automatic rollback on any failure.
              </p>
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <p style={{ marginTop: spacing.xl, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
          © {new Date().getFullYear()} Kementerian Pendidikan Negara Brunei Darussalam
        </p>
      </div>

      <ConfirmModal
        open={modalOpen}
        loading={loading}
        onConfirm={handleReset}
        onCancel={() => { if (!loading) setModalOpen(false); }}
      />

      {toast && (
        <Toast
          message={toast.message}
          variant={toast.variant}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
